import { Badge } from "@/components/ui/badge";
import {
  MessageSquare,
  Sparkles,
  Layers,
  MapPin,
  ShieldCheck,
  Zap,
  Users,
  FileText,
  Brain,
} from "lucide-react";
import { motion } from "framer-motion";

const features = [
  {
    icon: MessageSquare,
    title: "Chat-native, no UI to learn",
    description:
      "Every action — draft, edit, publish, report — happens in Claude, ChatGPT or Perplexity. No 14-screen Google Ads wizard.",
  },
  {
    icon: Sparkles,
    title: "Value-prop doc built from your homepage",
    description:
      "Upfixe scrapes your site and writes a 9-section brand brief. Ad copy is informed by who you actually are.",
  },
  {
    icon: Zap,
    title: "Budget-aware structuring",
    description:
      "Same prompt produces a structurally different campaign depending on your daily budget. Other tools just dump keywords; we structure for what you can spend.",
  },
  {
    icon: Brain,
    title: "Real Keyword Planner data, CPA-filtered",
    description:
      "We pull live Google Keyword Planner figures and drop anything where the CPC would mathematically break your target CPA — before you go live.",
  },
  {
    icon: Layers,
    title: "Multi-brand for agencies",
    description:
      "Register each client as a brand. Each gets its own value-prop doc and its own Google Ads sub-account binding under your MCC.",
  },
  {
    icon: MapPin,
    title: "Geo targeting below country level",
    description:
      "Describe a city, a region, or a radius — Upfixe maps it to the right Google Ads geo settings. \"15 miles around Austin\" works.",
  },
  {
    icon: ShieldCheck,
    title: "Draft-first publishing",
    description:
      "Campaigns ship to your Google Ads account paused. Nothing spends until you flip the switch in the Google Ads UI yourself.",
  },
  {
    icon: FileText,
    title: "Reporting by chat",
    description:
      '"How did we do last week?" Lifetime and date-ranged metrics across all your brands, no dashboard to navigate.',
  },
  {
    icon: Users,
    title: "Works in your favourite AI client",
    description:
      "Claude (free or paid), Perplexity, and ChatGPT (Business / Enterprise / Edu). The MCP server is the product — the AI is yours.",
  },
];

const FeaturesSection = () => {
  return (
    <section className="py-24 px-4 bg-muted/30">
      <div className="container mx-auto max-w-6xl">
        <div className="text-center mb-16">
          <Badge className="px-3 py-1 text-sm bg-primary/10 text-primary border-primary/20 mb-4">
            Features
          </Badge>
          <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-6">
            Everything an agency does. By chat.
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            We replaced the dashboard with a conversation. Here's what you can
            do without touching the Google Ads UI.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={index}
                className="bg-card rounded-xl p-6 border border-border hover:border-primary/30 hover:shadow-md transition-all group"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.06 }}
              >
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/15 transition-colors">
                  <Icon className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  {feature.title}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
